import React from 'react';
import { toast } from 'react-toastify';
import { TickCircle, CloseCircle, InfoCircle, Warning2 } from 'iconsax-react';

/**
 * Default toast configuration
 */
const defaultToastConfig = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'colored',
  closeButton: false,
};

/**
 * Show a success toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional toast options (optional)
 */
export const showSuccessToast = (message, options = {}) => {
  return toast.success(message, {
    ...defaultToastConfig,
    ...options,
    icon: <TickCircle variant="Outline" size="20" color="#389355" />,
  });
};

/**
 * Show an error toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional toast options (optional)
 */
export const showErrorToast = (message, options = {}) => {
  return toast.error(message, {
    ...defaultToastConfig,
    ...options,
    icon: <CloseCircle variant="Outline" size="20" color="#DE2927" />,
  });
};

/**
 * Show an info toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional toast options (optional)
 */
export const showInfoToast = (message, options = {}) => {
  return toast.info(message, {
    ...defaultToastConfig,
    ...options,
    icon: <InfoCircle variant="Outline" size="20" color="#2196F3" />,
  });
};

/**
 * Show a warning toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional toast options (optional)
 */
export const showWarningToast = (message, options = {}) => {
  return toast.warning(message, {
    ...defaultToastConfig,
    ...options,
    icon: <Warning2 variant="Outline" size="20" color="#FF9800" />,
  });
};

/**
 * Generic toast function for custom configurations
 * @param {string} type - Toast type ('success', 'error', 'info', 'warning')
 * @param {string} message - The message to display
 * @param {object} options - Additional toast options (optional)
 */
export const showToast = (type, message, options = {}) => {
  const toastFunctions = {
    success: showSuccessToast,
    error: showErrorToast,
    info: showInfoToast,
    warning: showWarningToast,
  };

  const toastFunction = toastFunctions[type];
  if (toastFunction) {
    return toastFunction(message, options);
  } else {
    console.warn(`Invalid toast type: ${type}`);
    return toast(message, { ...defaultToastConfig, ...options });
  }
};
